import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";
import NewsCard from "@/components/feature/NewsCard";
import VideoCard from "@/components/feature/VideoCard";
import VideoModal from "@/components/feature/VideoModal";
import type { Streamer, NewsItem, VideoItem, GalleryItem } from "@/types/content";
import { streamers } from "@/mocks/streamers";
import { news } from "@/mocks/news";
import { videos } from "@/mocks/videos";
import { gallery } from "@/mocks/gallery";
import PhotoGallery from "@/components/feature/PhotoGallery";
import ProfileHero from "@/pages/detail/components/ProfileHero";

const allStreamers = streamers as Streamer[];
const allNews = news as NewsItem[];
const allVideos = videos as VideoItem[];
const allGallery = gallery as GalleryItem[];

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const [playing, setPlaying] = useState<VideoItem | null>(null);

  const streamer = allStreamers.find((s) => s.id === id);

  const related = useMemo(() => {
    if (!streamer) return [];
    const sameType = allStreamers.filter((s) => s.type === streamer.type && s.id !== streamer.id);
    const others = allStreamers.filter((s) => s.type !== streamer.type && s.id !== streamer.id);
    return [...sameType, ...others].slice(0, 4);
  }, [streamer]);

  if (!streamer) {
    return (
      <>
        <Seo
          title="主播资料未找到 – 韩国女主播"
          description="未找到该主播的详细资料，前往分类列表浏览更多韩国女主播资讯与写真内容。"
          keywords="韩国女主播,主播列表,韩国主播资讯"
          canonical={`/detail/${id ?? ""}`}
        />
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 py-20 flex flex-col items-center text-center">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
            <i className="ri-user-unfollow-line text-3xl"></i>
          </span>
          <h1 className="font-heading font-semibold text-foreground-900 mb-2">没有找到这位主播</h1>
          <p className="text-sm text-foreground-500 mb-6">该主播可能已下线，或链接有误。</p>
          <Link
            to="/category"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回分类列表
          </Link>
        </div>
      </>
    );
  }

  const streamerNews = allNews.filter((n) => n.streamerId === streamer.id);
  const streamerVideos = allVideos.filter((v) => v.streamerId === streamer.id);
  const streamerGallery = allGallery.filter((g) => g.streamerId === streamer.id);

  const infoItems = [
    { label: "生日", value: streamer.birthday, icon: "ri-cake-2-line" },
    { label: "职业", value: streamer.position, icon: "ri-briefcase-4-line" },
    { label: "代表节目", value: streamer.program, icon: "ri-tv-2-line" },
    { label: "出道年份", value: `${streamer.debutYear} 年`, icon: "ri-calendar-check-line" },
    { label: "所在地区", value: streamer.region, icon: "ri-map-pin-line" },
    { label: "粉丝数", value: streamer.followers, icon: "ri-user-heart-line" },
  ];

  return (
    <>
      <Seo
        title={`${streamer.name}简介与动态 – 韩国女主播`}
        description={`${streamer.name}是${streamer.region}的${streamer.type}，代表节目《${streamer.program}》。查看${streamer.name}的最新新闻动态、视频精选与高清写真专题。`}
        keywords={`${streamer.name},韩国女主播,${streamer.type},${streamer.program},${streamer.name}动态,韩国主播写真`}
        canonical={`/detail/${streamer.id}`}
      />

      <div className="mx-auto max-w-[1440px] px-4 md:px-8 py-6 md:py-8">
        <Breadcrumb items={[{ label: "女主播分类", to: "/category" }, { label: streamer.name }]} />

        <div className="mt-4">
          <ProfileHero streamer={streamer} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mt-8">
          <div className="lg:col-span-2 space-y-10">
            <section>
              <SectionHeading title="详细资料" subtitle="主播核心档案信息" />
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {infoItems.map((item) => (
                  <div key={item.label} className="flex items-center gap-3 p-3 rounded-lg bg-background-100 border border-background-200/60">
                    <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-primary-600 text-background-50">
                      <i className={`${item.icon} text-lg`}></i>
                    </span>
                    <span className="min-w-0">
                      <span className="block text-xs text-foreground-500">{item.label}</span>
                      <span className="block text-sm font-medium text-foreground-900 truncate">{item.value}</span>
                    </span>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <SectionHeading title="最新新闻" subtitle={`${streamer.name}的相关资讯动态`} />
              {streamerNews.length > 0 ? (
                <div className="flex flex-col gap-3">
                  {streamerNews.map((item) => (
                    <NewsCard key={item.id} item={item} horizontal />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-10 rounded-lg bg-background-100 text-center">
                  <i className="ri-newspaper-line text-3xl text-foreground-400 mb-2"></i>
                  <p className="text-sm text-foreground-500">暂无相关新闻资讯</p>
                </div>
              )}
            </section>

            <section>
              <SectionHeading title="视频精选" subtitle="采访、节目片段与写真 MV" accent />
              {streamerVideos.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {streamerVideos.map((video) => (
                    <VideoCard key={video.id} video={video} onPlay={setPlaying} />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-10 rounded-lg bg-background-100 text-center">
                  <i className="ri-video-line text-3xl text-foreground-400 mb-2"></i>
                  <p className="text-sm text-foreground-500">暂无相关视频</p>
                </div>
              )}
            </section>

            <section>
              <SectionHeading title="个人写真图库" subtitle="点击图片可放大查看，支持左右切换" />
              <PhotoGallery items={streamerGallery} />
            </section>
          </div>

          <aside className="lg:col-span-1 space-y-6">
            <div className="lg:sticky lg:top-20 space-y-6">
              <div className="rounded-lg bg-background-100 border border-background-200/70 p-5">
                <h3 className="flex items-center gap-2 font-heading font-semibold text-foreground-950 mb-4">
                  <i className="ri-bar-chart-2-line text-accent-500"></i>
                  关注度数据
                </h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="text-foreground-600">综合热度</span>
                      <span className="font-semibold text-accent-600">{streamer.heat}</span>
                    </div>
                    <div className="h-2 rounded-full bg-background-200 overflow-hidden">
                      <div className="h-full rounded-full bg-accent-500" style={{ width: `${streamer.heat}%` }}></div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <div className="rounded-md bg-background-50 p-3 text-center">
                      <p className="text-lg font-heading font-bold text-primary-600">{streamer.followers}</p>
                      <p className="text-xs text-foreground-500 mt-0.5">粉丝关注</p>
                    </div>
                    <div className="rounded-md bg-background-50 p-3 text-center">
                      <p className="text-lg font-heading font-bold text-primary-600">{streamerNews.length}</p>
                      <p className="text-xs text-foreground-500 mt-0.5">相关资讯</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-background-100 border border-background-200/70 p-5">
                <h3 className="flex items-center gap-2 font-heading font-semibold text-foreground-950 mb-4">
                  <i className="ri-magic-line text-accent-500"></i>
                  相关推荐
                </h3>
                <div className="flex flex-col gap-3">
                  {related.map((s) => (
                    <Link
                      key={s.id}
                      to={`/detail/${s.id}`}
                      className="group flex items-center gap-3 rounded-lg p-2 hover:bg-background-50 transition-colors"
                    >
                      <span className="w-12 h-12 shrink-0 rounded-full overflow-hidden bg-background-100">
                        <img
                          src={s.avatar}
                          alt={s.name}
                          title={`${s.name} 韩国女主播`}
                          className="w-full h-full object-cover object-top"
                        />
                      </span>
                      <span className="flex flex-col min-w-0 flex-1">
                        <span className="text-sm font-medium text-foreground-900 group-hover:text-accent-600 transition-colors truncate">
                          {s.name}
                        </span>
                        <span className="text-xs text-foreground-500 truncate">{s.program}</span>
                      </span>
                      <span className="text-xs text-accent-600 whitespace-nowrap">
                        <i className="ri-fire-fill"></i>
                        {s.heat}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <VideoModal video={playing} onClose={() => setPlaying(null)} />
    </>
  );
}